import { useMutation, useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Plus, Search } from 'lucide-react'
import { useMemo, useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { useTranslate } from 'shared/lib/i18n'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpc } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { FacetedFilter } from 'shared/components/FacetedFilter'
import { PageFrame } from 'shared/components/page-frame'
import { Hint } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from 'shared/components/ui/select'
import { Sheet, SheetContent } from 'shared/components/ui/sheet'
import { Tooltip, TooltipContent, TooltipTrigger } from 'shared/components/ui/tooltip'
import { useDataTable } from 'shared/hooks/use-data-table'
import { formatDate } from 'shared/lib/utils/date'
import { locationIdFailureKey } from '../lib/location-id-failure'
import { LocationSheet } from './LocationSheet'

/**
 * Locations — the admin's list of every place. The network is ONE Location
 * entity whose type — Point, hub or central cashbox — decides its behaviour
 * (ADR-0028). The region is a label only. Each place has one **Location id** («ID
 * места», e.g. `TT-107`) — the same in TezGo, in `back` and at every partner, unique
 * (ticket 50): a taken or empty one is refused in words. The record is addressed by
 * its database `_id`.
 *
 * The places are one table — the id, the name, the type, and for a Point whether it
 * is on TezGo and whether it is open — with a search and a type filter over it. A
 * click on a row opens the place in a side panel: its switch onto TezGo, its
 * closure, its week, its inventory and its journal. «Новое место» and «Изменить»
 * (the id and the type) are dialogs.
 */

type Location = RouterOutputs['locations']['List'][number]
type LocationType = RouterInputs['locations']['Create']['type']

const LOCATION_TYPES: LocationType[] = ['point', 'hub', 'central-cashbox']

/** Chooses a Location type; every option is the type's translated name. */
function LocationTypeSelect({
	value,
	onChange,
	id,
}: {
	value: LocationType
	onChange: (type: LocationType) => void
	id?: string
}) {
	const { t } = useTranslate()
	return (
		<Select value={value} onValueChange={(v) => onChange(v as LocationType)}>
			<SelectTrigger id={id} className='w-full' aria-label={t('locations.typePlaceholder')}>
				<SelectValue placeholder={t('locations.typePlaceholder')} />
			</SelectTrigger>
			<SelectContent>
				{LOCATION_TYPES.map((type) => (
					<SelectItem key={type} value={type}>
						{t(`locationType.${type}`)}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

export function Locations() {
	const { t } = useTranslate()
	const [search, setSearch] = useState('')
	const [types, setTypes] = useState<string[] | undefined>()
	const [creating, setCreating] = useState(false)
	const [editingId, setEditingId] = useState<string | null>(null)
	const [openId, setOpenId] = useState<string | null>(null)

	const locationsQuery = useQuery(trpc.locations.List.queryOptions())
	const locations = locationsQuery.data ?? []
	const refetch = () => locationsQuery.refetch()
	const open = locations.find((l) => l._id === openId) ?? null
	const editing = locations.find((l) => l._id === editingId) ?? null

	const needle = search.trim().toLowerCase()
	const rows = useMemo(
		() =>
			(locationsQuery.data ?? []).filter(
				(l) =>
					(!types?.length || types.includes(l.type)) &&
					(!needle || l.id.toLowerCase().includes(needle) || l.name.toLowerCase().includes(needle)),
			),
		[locationsQuery.data, types, needle],
	)

	const columns: ColumnDef<Location>[] = [
		{
			id: 'id',
			header: t('locations.idLabel'),
			cell: ({ row }) => (
				<span className='font-mono font-semibold' data-testid='location-id'>
					{row.original.id}
				</span>
			),
		},
		{ id: 'name', header: t('locations.colName'), cell: ({ row }) => row.original.name },
		{ id: 'type', header: t('locations.colType'), cell: ({ row }) => t(`locationType.${row.original.type}`) },
		{
			id: 'region',
			header: t('locations.colRegion'),
			cell: ({ row }) => <span className='text-muted-foreground'>{row.original.region}</span>,
		},
		{
			id: 'tezgo',
			header: t('locations.colTezgo'),
			cell: ({ row: { original: l } }) => <TezgoMark location={l} />,
		},
		{
			id: 'state',
			header: t('locations.colState'),
			cell: ({ row: { original: l } }) => <StateMark location={l} />,
		},
		{
			id: 'actions',
			header: '',
			meta: { cellClassName: 'w-px text-right' },
			cell: ({ row: { original: l } }) => (
				// The menu's clicks stay here, not on the row under it.
				<div onClick={(e) => e.stopPropagation()}>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm' aria-label={t('locations.actions')} data-testid={`location-menu-${l.id}`}>
								<MoreHorizontal />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem onSelect={() => setOpenId(l._id)}>{t('locations.details')}</DropdownMenuItem>
							<DropdownMenuItem onSelect={() => setEditingId(l._id)} data-testid='location-edit'>
								{t('locations.edit')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			),
		},
	]

	const { table } = useDataTable({
		data: rows,
		columns,
		rowCount: rows.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		// The rows are filtered here: a new list must not reset the page in a loop.
		autoResetPageIndex: false,
		getRowId: (l) => l._id,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<PageFrame
			title='locations.title'
			actions={
				<Button data-testid='location-new' onClick={() => setCreating(true)}>
					<Plus />
					{t('locations.new')}
				</Button>
			}
		>
			<div className='flex flex-col gap-3'>
				<div className='flex flex-wrap items-center gap-2'>
					<div className='relative w-64'>
						<Search className='pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground' />
						<Input
							className='h-8 pl-8'
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={t('locations.search')}
							aria-label={t('locations.search')}
						/>
					</div>
					<FacetedFilter
						title={t('locations.colType')}
						values={types}
						setValues={setTypes}
						options={LOCATION_TYPES.map((type) => ({ value: type, label: t(`locationType.${type}`) }))}
					/>
				</div>
				{locationsQuery.isError ? (
					<p className='text-sm text-destructive'>{locationsQuery.error.message}</p>
				) : locationsQuery.isLoading ? (
					<p className='text-sm text-muted-foreground'>{t('locations.loading')}</p>
				) : (
					<DataTable
						table={table}
						withPagination={false}
						emptyText={t('locations.empty')}
						row={{
							attributes: (l) => ({ 'data-testid': `location-row-${l.id}` }),
							onRowClick: (l) => setOpenId(l._id),
						}}
					/>
				)}
			</div>

			<CreateLocationDialog open={creating} onOpenChange={setCreating} onCreated={refetch} />
			<EditLocationDialog location={editing} onClose={() => setEditingId(null)} onSaved={refetch} />

			<Sheet open={open !== null} onOpenChange={(o) => !o && setOpenId(null)}>
				<SheetContent className='overflow-y-auto data-[side=right]:sm:max-w-2xl'>
					{open && (
						<LocationSheet
							location={open}
							points={locations}
							onChanged={refetch}
							onEdit={() => setEditingId(open._id)}
						/>
					)}
				</SheetContent>
			</Sheet>
		</PageFrame>
	)
}

/** A Point on TezGo or not, and its drain; nothing for a hub or a cashbox. */
function TezgoMark({ location: l }: { location: Location }) {
	const { t } = useTranslate()
	if (l.type !== 'point') return null
	return (
		<div className='flex items-center gap-1.5' data-testid='location-tezgo' data-on={l.migrated ? 'yes' : 'no'}>
			{l.migrated ? (
				<Badge variant='outline'>{t('cutover.statusMigrated')}</Badge>
			) : (
				<span className='text-muted-foreground'>{t('cutover.statusNotMigrated')}</span>
			)}
			{l.drain && (
				<Badge variant='secondary'>
					{t('locations.drainUntil', { date: formatDate(l.drain.expectedEndDate) })}
				</Badge>
			)}
		</div>
	)
}

/** A Point open, closed for now (its reason on hover) or closed for good. */
function StateMark({ location: l }: { location: Location }) {
	const { t } = useTranslate()
	if (l.type !== 'point') return null
	if (l.closedForGood) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<Badge variant='destructive' data-testid='location-closed-for-good'>
						{t('locations.closedForGood', { recipient: l.closedForGood.recipientCode })}
					</Badge>
				</TooltipTrigger>
				<TooltipContent>{l.closedForGood.comment}</TooltipContent>
			</Tooltip>
		)
	}
	if (l.temporarilyClosed) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<Badge variant='secondary' data-testid='location-closed'>
						{t('locations.closedNow')}
					</Badge>
				</TooltipTrigger>
				<TooltipContent>{l.temporarilyClosed.comment}</TooltipContent>
			</Tooltip>
		)
	}
	return <span className='text-muted-foreground'>{t('pointClosure.open')}</span>
}

function CreateLocationDialog({
	open,
	onOpenChange,
	onCreated,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	onCreated: () => void
}) {
	const { t } = useTranslate()
	const [name, setName] = useState('')
	const [locationId, setLocationId] = useState('')
	const [type, setType] = useState<LocationType>('point')
	const [region, setRegion] = useState('')
	const [error, setError] = useState<string | null>(null)

	const createMutation = useMutation({
		...trpc.locations.Create.mutationOptions(),
		onSuccess: () => {
			setName('')
			setLocationId('')
			setRegion('')
			setError(null)
			onOpenChange(false)
			onCreated()
			toast.success(t('locations.created'))
		},
		onError: (failure) => setError(t(locationIdFailureKey(failure))),
	})

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault()
		setError(null)
		createMutation.mutate({ name, id: locationId, type, region })
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='sm:max-w-md' aria-describedby={undefined} data-testid='location-create-dialog'>
				<DialogHeader>
					<DialogTitle>{t('locations.new')}</DialogTitle>
				</DialogHeader>
				<form className='grid gap-4' onSubmit={handleSubmit}>
					<div className='grid gap-1.5'>
						<Label htmlFor='location-name-new'>{t('locations.colName')}</Label>
						<Input
							id='location-name-new'
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t('locations.namePlaceholder')}
							required
						/>
					</div>
					<div className='grid grid-cols-2 gap-4'>
						<div className='grid gap-1.5'>
							<div className='flex items-center gap-1.5'>
								<Label htmlFor='location-id-new'>{t('locations.idLabel')}</Label>
								<Hint>{t('locations.idHint')}</Hint>
							</div>
							<Input
								id='location-id-new'
								data-testid='location-create-id'
								className='font-mono'
								value={locationId}
								onChange={(e) => {
									setLocationId(e.target.value.toUpperCase())
									setError(null)
								}}
								placeholder={t('locations.idPlaceholder')}
								required
							/>
						</div>
						<div className='grid gap-1.5'>
							<Label htmlFor='location-region-new'>{t('locations.colRegion')}</Label>
							<Input
								id='location-region-new'
								value={region}
								onChange={(e) => setRegion(e.target.value.toUpperCase())}
								placeholder={t('locations.regionPlaceholder')}
								required
							/>
						</div>
					</div>
					<div className='grid gap-1.5'>
						<Label htmlFor='location-type-new'>{t('locations.colType')}</Label>
						<LocationTypeSelect id='location-type-new' value={type} onChange={setType} />
					</div>
					{error && (
						<p role='alert' className='text-sm text-destructive' data-testid='location-create-error'>
							{error}
						</p>
					)}
					<DialogFooter>
						<Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
							{t('locations.cancel')}
						</Button>
						<Button
							disabled={createMutation.isPending || !name.trim() || !locationId.trim() || !region.trim()}
						>
							{t('locations.create')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}

/**
 * Changes a place's Location id (ticket 50) and its type (ADR-0028). The id is one
 * across the network, so a taken one is refused by the server and named here in
 * words, as is an empty one; the field keeps what the admin typed until it is saved
 * or corrected. Only what changed is sent.
 */
function EditLocationDialog({
	location,
	onClose,
	onSaved,
}: {
	location: Location | null
	onClose: () => void
	onSaved: () => void
}) {
	const { t } = useTranslate()
	const [value, setValue] = useState('')
	const [type, setType] = useState<LocationType>('point')
	const [error, setError] = useState<string | null>(null)

	const [seeded, setSeeded] = useState<string | null>(null)
	if ((location?._id ?? null) !== seeded) {
		setSeeded(location?._id ?? null)
		if (location) {
			setValue(location.id)
			setType(location.type)
			setError(null)
		}
	}

	const save = useMutation({
		...trpc.locations.Update.mutationOptions(),
		onSuccess: () => {
			onSaved()
			onClose()
			toast.success(t('locations.saved'))
		},
		onError: (failure) => setError(t(locationIdFailureKey(failure))),
	})

	if (!location) return null
	const idChanged = value.trim() !== location.id
	const typeChanged = type !== location.type

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className='sm:max-w-md' aria-describedby={undefined} data-testid='location-edit-dialog'>
				<DialogHeader>
					<DialogTitle>{t('locations.editTitle', { id: location.id })}</DialogTitle>
				</DialogHeader>
				<form
					className='grid gap-4'
					onSubmit={(event) => {
						event.preventDefault()
						save.mutate({
							_id: location._id,
							...(idChanged ? { id: value } : {}),
							...(typeChanged ? { type } : {}),
						})
					}}
				>
					<div className='grid gap-1.5'>
						<div className='flex items-center gap-1.5'>
							<Label htmlFor='location-id-edit'>{t('locations.idLabel')}</Label>
							<Hint>{t('locations.idHint')}</Hint>
						</div>
						<Input
							id='location-id-edit'
							data-testid='location-id-input'
							className='font-mono'
							value={value}
							onChange={(e) => {
								setValue(e.target.value.toUpperCase())
								setError(null)
							}}
							placeholder={t('locations.idPlaceholder')}
						/>
					</div>
					<div className='grid gap-1.5'>
						<Label htmlFor='location-type-edit'>{t('locations.colType')}</Label>
						<LocationTypeSelect id='location-type-edit' value={type} onChange={setType} />
					</div>
					{error && (
						<p role='alert' className='text-sm text-destructive' data-testid='location-id-error'>
							{error}
						</p>
					)}
					<DialogFooter>
						<Button type='button' variant='outline' onClick={onClose}>
							{t('locations.cancel')}
						</Button>
						<Button data-testid='location-id-save' disabled={save.isPending || (!idChanged && !typeChanged)}>
							{t('locations.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}
